import { strRemoveSpaces, translation as t } from "../../../../inc/helpers";

export default class Input {
    constructor({ container, input, label, error }) {
        for (const [key, value] of Object.entries(arguments[0]))
            this[`_${key}`] = value;

        this._name = this._container.dataset.name;
        this._id = this._name;
        this._value = this._container.dataset.value ?? null;
        this._valuePrev = this._value;

        if (this._value && this._input)
            this._input.value = this._value;

        this._type = this._container.dataset.type;

        this._required = this._container.dataset?.required === "true" ?? false;
        this._readonly = this._container.dataset?.readonly === "true" ?? false;
        this._disabled = this._container.dataset?.disabled === "true" ?? false;
        this._hidden = this._container.dataset?.hidden === "true" ?? false;

        this._minlength = this._container.hasAttribute("data-minlength")
            ? parseInt(this._container.getAttribute("data-minlength"))
            : null;
        this._maxlength = this._container.hasAttribute("data-maxlength")
            ? parseInt(this._container.getAttribute("data-maxlength"))
            : null;
        this._pattern = this._container.hasAttribute("data-pattern")
            ? new RegExp(this._container.getAttribute("data-pattern"))
            : null;

        this._controls = this._container.dataset?.controls
            ? this._container.dataset.controls.split(",").map((name) => name.trim())
            : [];
        this._onvalid = this._container.dataset?.onValid
            ? this.setupActions(
                strRemoveSpaces(this._container.dataset.onValid),
                !this._container.dataset.onValid.includes(":") && this._controls
            )
            : [];
        this._oninvalid = this._container.dataset?.onInvalid
            ? this.setupActions(
                strRemoveSpaces(this._container.dataset.onInvalid),
                !this._container.dataset.onInvalid.includes(":") && this._controls
            )
            : [];
        this._onchange = this._container.dataset?.onChange
            ? this.setupActions(
                strRemoveSpaces(this._container.dataset.onChange),
                !this._container.dataset.onChange.includes(":") && this._controls
            )
            : [];

        this._initialSetup = {};
        this._evOnChange = null;
        this._evOnBlur = null;

        this._valid = { flag: null, msg: "" };
        this._validPrev = { ...this._valid };

        this._debounce = null;
        this._debounceTiming = 300;
    }

    setupActions(str, targets = []) {
        const _targets = [];
        const extract = (name, actions) => {
            const target = { name, actions };
            return target;
        }

        if (str.includes(";")) {
            str.split(";").forEach((group) => {
                const [name, actions] = group.split(":");
                _targets.push(extract(name, actions.split(",")));
            });

        } else if (str.includes(":")) {
            const [name, actions] = str.split(":");
            _targets.push(extract(name, actions.split(",")));

        } else if (targets?.length > 0) {
            targets.forEach((t) => {
                _targets.push(extract(t, str.split(",")));
            });
        }

        return _targets;
    }

    init() {
        this.saveInitialSetup();
        this.initEvs();

        if (this._value) {
            this._valid = this._validate(this._value);
            this.handleChange();
            this.toggleValidation();
        }
    }

    saveInitialSetup() {
        for (const [key, value] of Object.entries(this))
            this._initialSetup[key] = value;
    }

    initEvs() {
        this._evOnChange = (e) => this.onChange(e);
        this._evOnBlur = (e) => this.onBlur(e);

        this._input.addEventListener("input", this._evOnChange);
        this._input.addEventListener("blur", this._evOnBlur);
        this._input.addEventListener("keydown", (e) => {
            if (e.key === "Enter") e.preventDefault();
        });
    }

    onChange(e) {
        if (this._readonly || this._disabled) return;

        this._value = this._input.value;
        this._valid = this._validate(this._value);

        this.handleChange();
        this.handleDispatch();
    }

    onBlur(e) {
        if (this._readonly || this._disabled) return;

        this._valid = this._validate(this._value);
        this.toggleValidation();

        if (this._valid.flag === null) return;

        if (this._valid.flag && this._onvalid.length > 0) {
            this._cbackControls?.(this._onvalid, this);

        } else if (!this._valid.flag && this._oninvalid.length > 0) {
            this._cbackControls?.(this._oninvalid, this);
        }

        this.handleDispatch();
    }

    _validate(value) {
        const _value = strRemoveSpaces(value);

        if (!this._required && _value === "") return { flag: null, msg: "" };

        if (this._required && _value === "")
            return { flag: false, msg: t("ERROR.FieldMandatory") };
        
        if (this._pattern && !this._pattern.test(_value))
            return { flag: false, msg: t("ERROR.InvalidFormat") };

        if (this._minlength && _value.length < this._minlength)
            return {
                flag: false,
                msg: t("ERROR.MinChars", [this._minlength]),
            };

        if (this._maxlength && _value.length > this._maxlength)
            return {
                flag: false,
                msg: t("ERROR.MaxChars", [this._maxlength]),
            };

        return { flag: true, msg: "" };
    }

    toggleValidation() {
        if (this._valid.flag === null) {
            this.resetValidation();
            return;
        }

        if (!this._valid.flag) {
            this.handleInvalid(this._valid.msg);
            return;
        }

        this.handleValid();
    }

    handleChange() {
        if (!(this._onchange.length > 0)) return;
        this._cbackControls?.(this._onchange, this);
    }

    handleDispatch() {
        if (
            (this._valuePrev !== this._value)
            || (this._validPrev.flag !== this._valid.flag)
        )
            this._dispatcher?.(this);

        this._valuePrev = this._value;
        this._validPrev = { ...this._valid };
    }

    handleInvalid(msg = "") {
        this._container.classList.remove("is-valid");

        if (!this._container.classList.contains("is-invalid"))
            this._container.classList.add("is-invalid");

        this._error.textContent = msg;
    }

    handleValid() {
        this._container.classList.remove("is-invalid");

        if (!this._container.classList.contains("is-valid"))
            this._container.classList.add("is-valid");
    }

    resetValidation() {
        this._container.classList.remove("is-invalid", "is-valid");
    }

    reset() {
        for (const [key, value] of Object.entries(this._initialSetup)) {
            if (key === "_initialSetup") continue;
            this[key] = value;
        }

        this.resetValidation();

        if (this._input)
            this._input.value = this._value;

        const hasValue =
            Array.isArray(this._value)
                ? this._value.length > 0
                : this._value;

        if (hasValue) {
            this._valid = this._validate(this._value);
            this.toggleValidation();
        }

        this._required
            ? this._container.setAttribute("data-required", "true")
            : this._container.removeAttribute("data-required");

        this._disabled
            ? this._container.setAttribute("data-disabled", "true")
            : this._container.removeAttribute("data-disabled");

        this._hidden
            ? this._container.setAttribute("data-hidden", "true")
            : this._container.removeAttribute("data-hidden");

        this._readonly
            ? this._container.setAttribute("data-readonly", "true")
            : this._container.removeAttribute("data-readonly");
    }

    get value() {
        return this._value;
    }

    set value(value) {
        this._value = value;

        if (this._input)
            this._input.value = this._value;

        this._valid = this._validate(this._value);
        this.toggleValidation();
    }

    get name() {
        return this._name;
    }
    set name(name) {
        this._name = name;
    }

    get id() {
        return this._id;
    }
    set id(id) {
        this._id = id;
    }

    get type() {
        return this._type;
    }
    set type(type) {
        this._type = type;
    }

    get required() {
        return this._required;
    }
    set required(bool) {
        this._required = bool;
        this._required
            ? this._container.setAttribute("data-required", "true")
            : this._container.removeAttribute("data-required");
    }

    get disabled() {
        return this._disabled;
    }
    set disabled(bool) {
        this._disabled = bool;
        this._disabled
            ? this._container.setAttribute("data-disabled", "true")
            : this._container.removeAttribute("data-disabled");
    }

    get hidden() {
        return this._hidden;
    }

    set hidden(bool) {
        this._hidden = bool;
        this._hidden
            ? this._container.setAttribute("data-hidden", "true")
            : this._container.removeAttribute("data-hidden");
    }

    get readonly() {
        return this._readonly;
    }
    set readonly(bool) {
        this._readonly = bool;
        this._readonly
            ? this._container.setAttribute("data-readonly", "true")
            : this._container.removeAttribute("data-readonly");
    }

    get minlength() {
        return this._minlength;
    }
    set minlength(number) {
        this._minlength = number;
    }

    get maxlength() {
        return this._maxlength;
    }
    set maxlength(number) {
        this._maxlength = number;
    }

    get valid() {
        this._valid = this._validate(this._value);
        return this._valid;
    }
    set valid({ flag, msg = "" }) {
        this._valid.flag = flag;
        this._valid.msg = msg;
        clearTimeout(this._debounce);
        this._debounce = setTimeout(
            () => this.toggleValidation(),
            this._debounceTiming
        );
    }

    get controls() {
        return this._controls;
    }
    set controls(names) {
        this._controls = names;
    }

    get cbackControls() {
        return this._cbackControls;
    }
    set cbackControls(func) {
        this._cbackControls = func;
    }

    get dispatcher() {
        return this._dispatcher;
    }

    set dispatcher(func) {
        this._dispatcher = func;
    }

    get container() {
        return this._container;
    }
    
    get error() {
        return this._error;
    }

    set error(error) {
        return this._error = error;
    }

    get validate() {
        return this._validate;
    }

    set validate(func) {
        this._validate = func;
    }
}